import { useState, useEffect, useCallback } from "react";
import { LAST_SAVED_SNAPSHOT_KEY } from "./useSettings"; // Import the key

const DATA_KEYS_TO_MONITOR = ["tasks", "todoLists", "dailyTasks", "settings"];

export function useDataChangeDetector(
  // Pass current state values from App.js to avoid direct localStorage reads in every check,
  // as localStorage reads can be slow if done too frequently.
  // These props will be used to construct the current snapshot.
  currentTasks,
  currentTodoLists,
  currentDailyTasks,
  currentSettings
) {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const getCurrentDataSnapshot = useCallback(() => {
    const currentData = {
      tasks: currentTasks,
      todoLists: currentTodoLists,
      dailyTasks: currentDailyTasks,
      settings: currentSettings,
    };
    return JSON.stringify(currentData);
  }, [currentTasks, currentTodoLists, currentDailyTasks, currentSettings]);

  const checkForChanges = useCallback(() => {
    const lastSavedSnapshot = localStorage.getItem(LAST_SAVED_SNAPSHOT_KEY);
    const currentSnapshot = getCurrentDataSnapshot();

    if (!lastSavedSnapshot) {
      // If no snapshot exists, consider it "unsaved" if there's any actual data.
      // This logic might need refinement: if it's a fresh app, is it "unsaved" or "pristine"?
      // For now, any data without a snapshot means unsaved.
      const dataExists =
        (currentTasks && currentTasks.length > 0) ||
        (currentTodoLists && currentTodoLists.length > 0) ||
        (currentDailyTasks && currentDailyTasks.length > 0) ||
        (currentSettings &&
          Object.keys(currentSettings).length > 0 && // Check if settings are different from initial defaults if needed
          (currentSettings.useTags !== false ||
            currentSettings.useNotes !== false ||
            currentSettings.clockFontTheme !== "default")); // etc.
      // A simpler check for settings: just see if it's not the initial empty-ish object from useSettings
      // For now, if any key in settings has a non-default value, consider it data.
      // This part is tricky without knowing the true "empty" state.
      // The most robust is just comparing stringified current data to stringified initial data.
      // But for "unsaved since last download", this logic is okay.

      setHasUnsavedChanges(dataExists);
      if (process.env.NODE_ENV === "development") {
        console.log(
          "[DataChangeDetector] No snapshot found. Data exists:",
          dataExists,
          "Unsaved changes set to:",
          dataExists
        );
      }
      return;
    }

    const changed = currentSnapshot !== lastSavedSnapshot;
    setHasUnsavedChanges(changed);
    if (process.env.NODE_ENV === "development") {
      // console.log("[DataChangeDetector] Checked for changes. Unsaved:", changed);
      // if (changed) {
      //   console.log("[DataChangeDetector] Current snapshot differs from last saved.");
      // }
    }
  }, [
    getCurrentDataSnapshot,
    currentTasks,
    currentTodoLists,
    currentDailyTasks,
    currentSettings,
  ]);

  // Effect to run check when component mounts or data dependencies change
  useEffect(() => {
    checkForChanges();
  }, [checkForChanges]); // Re-check when data passed to the hook changes

  // This is the function App.js will call to manually trigger a re-check
  const refreshChangeStatus = useCallback(() => {
    checkForChanges();
  }, [checkForChanges]);

  return { hasUnsavedChanges, refreshChangeStatus };
}
